import {
  Autocomplete,
  Box,
  Grid,
  IconButton,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { DateTime } from 'luxon';
import React, { useState } from 'react';
import { CaretRight, ChevronUp, Plus } from 'react-bootstrap-icons';
import { ChatType, Property } from 'server-sdk/types';
import { compress } from 'src/utils/compress';
import { usePhotoChat } from 'ui-sdk/src/hooks/usePhotoUpload';
import { useRouter } from 'ui-sdk/src/router';
import { theme } from 'ui-sdk/src/theme';
import { MarbleButton } from '..';
import { UploadButton } from '../inputs/UploadButton';
import { ActiveChatTitle } from './ActiveChatTitle';
import { MessageList } from './MessageList';
import { MessageListItemNew } from './MessageListItem';

interface ActiveChatProps {
  chatId?: number;
  onSubmit: (message: string) => void;
  active?: boolean;
  isLoadingSuggestions?: boolean;
  chatSuggestions?: string[];
  isTyping?: boolean;
  textChat?: boolean;
  email?: boolean;
}

export const ActiveChat: React.FC<ActiveChatProps> = ({
  chatId,
  children,
  onSubmit,
  active,
  isLoadingSuggestions,
  chatSuggestions,
  isTyping,
  textChat,
  email,
}) => {
  const muiTheme = useTheme();
  const isSmallScreen = useMediaQuery(muiTheme.breakpoints.only('xs'));

  const hasSuggestions = (chatSuggestions?.length ?? 0) > 0;
  const [messageInput, setMessageInput] = useState('');
  const [openSuggestions, setOpenSuggestions] = useState(true);
  const handleSubmit = () => {
    onSubmit(messageInput);
    setMessageInput('');
  };
  const chatType = textChat ? ChatType.TextMessage : ChatType.MC;
  const { onChange } = usePhotoChat(chatId, chatType, compress);

  return active ? (
    <div
      style={{
        flex: 2,
        overflow: 'none',
        width: '100%',
        height: '100%',
        position: 'relative',
        border: 'none',
      }}
    >
      {children}
      <div
        style={{
          bottom: 0,
          zIndex: 1,
          width: '100%',
          paddingTop: theme.spacing(2),
          paddingBottom: theme.spacing(2),
          paddingLeft: theme.spacing(4),
          paddingRight: theme.spacing(4),
        }}
      >
        <Grid
          columns={50}
          container
          alignItems="center"
          justifyContent="space-between"
          alignContent="center"
        >
          <Grid
            item
            xs={37}
            md={44}
            sx={{ paddingLeft: { xs: 0, md: theme.spacing(3) } }}
          >
            <Autocomplete
              loading={isLoadingSuggestions}
              inputValue={messageInput}
              open={hasSuggestions && openSuggestions}
              onOpen={() => {
                if (hasSuggestions) {
                  setOpenSuggestions(true);
                }
              }}
              onInputChange={(event: any, newValue: string | null) => {
                if (event?.type === 'change' || event?.type === 'click') {
                  // click for the autocomplete suggestions
                  setMessageInput(newValue);
                }
              }}
              onKeyPress={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault();
                  handleSubmit();
                }
              }}
              popupIcon={null}
              onClose={() => setOpenSuggestions(false)}
              disablePortal
              id="chat-suggestions"
              sx={{ width: '100%' }}
              options={chatSuggestions}
              renderInput={(params) => (
                <Box>
                  <Typography
                    fontSize={12}
                    color="gray"
                    sx={{
                      pl: 2,
                      pb: 1,
                    }}
                  >
                    {isTyping ? 'Nancy is typing...' : ''}
                  </Typography>
                  <TextField {...params} multiline rows={2} />
                </Box>
              )}
            />
          </Grid>
          <Grid
            item
            xs={13}
            md={6}
            sx={{ paddingRight: 0, paddingLeft: 0 }}
            textAlign="right"
            justifyContent="end"
          >
            <Grid container justifyContent="end" alignItems="center">
              <Grid item p={0} alignContent="center">
                {isSmallScreen && (
                  <MarbleButton
                    icon={<ChevronUp size={20} />}
                    size="small"
                    onClick={() => {
                      handleSubmit();
                    }}
                    style={{ padding: theme.spacing(1) }}
                  />
                )}
                {!isSmallScreen && (
                  <MarbleButton
                    title="Send"
                    size="small"
                    onClick={() => {
                      handleSubmit();
                    }}
                    style={{ padding: theme.spacing(1) }}
                  />
                )}
              </Grid>
              <Grid item p={0}>
                {chatId && !email && (
                  <UploadButton
                    accept="image/jpeg, image/png, application/pdf"
                    onChange={onChange}
                    labelStyle={{
                      display: 'inline-flex',
                      textAlign: 'center',
                    }}
                  >
                    <IconButton
                      component="span"
                      color="primary"
                      size="large"
                      style={{ padding: 5 }}
                    >
                      <Plus size={isSmallScreen ? 20 : 33} />
                    </IconButton>
                  </UploadButton>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        {/* <div style={{ width: '100%', display: 'flex' }}>
          
          
          {chatId && !email && (
            <UploadButton
              accept="image/jpeg, image/png"
              onChange={onChange}
              labelStyle={{
                display: 'inline-flex',
                textAlign: 'center',
              }}
            >
              <IconButton
                component="span"
                color="primary"
                size="large"
                style={{ padding: theme.spacing(1) }}
              >
                <CameraFill size={33} />
              </IconButton>
            </UploadButton>
          )}
        </div> */}
      </div>
    </div>
  ) : (
    <div />
  );
};

export interface LeasingReport {
  property?: Property;
  property_id?: number;
  reports: LeasingReportWeek[];
}

export interface LeasingReportWeek {
  startDate?: number;
  numInquiries?: number;
  numScheduledTours?: number;
  numCompletedTours?: number;
  numApplications?: number;
}

interface ActiveLeasingReportProps {
  leasingReport?: LeasingReport;
  supportChatId?: number;
  demo?: boolean;
}

const currentDate = DateTime.now();
const demoReports: LeasingReportWeek[] = [
  {
    startDate: DateTime.fromObject({
      weekNumber: currentDate.weekNumber - 4,
      weekday: currentDate.weekday + 2,
    }).toMillis(),
    numInquiries: 10,
    numScheduledTours: 5,
    numCompletedTours: 1,
    numApplications: 1,
  },
  {
    startDate: DateTime.fromObject({
      weekNumber: currentDate.weekNumber - 3,
      weekday: currentDate.weekday + 2,
    }).toMillis(),
    numInquiries: 5,
    numScheduledTours: 3,
    numCompletedTours: 3,
    numApplications: 2,
  },
  {
    startDate: DateTime.fromObject({
      weekNumber: currentDate.weekNumber - 2,
      weekday: currentDate.weekday + 2,
    }).toMillis(),
    numInquiries: 6,
    numScheduledTours: 2,
    numCompletedTours: 4,
    numApplications: 4,
  },
  {
    startDate: DateTime.fromObject({
      weekNumber: currentDate.weekNumber - 1,
      weekday: currentDate.weekday + 2,
    }).toMillis(),
    numInquiries: 2,
    numScheduledTours: 1,
    numCompletedTours: 0,
    numApplications: 1,
  },
  {
    startDate: null,
    numInquiries: 23,
    numScheduledTours: 11,
    numCompletedTours: 7,
    numApplications: 8,
  },
];

export const ActiveLeasingReport: React.FC<ActiveLeasingReportProps> = ({
  leasingReport,
  supportChatId,
  demo,
}) => {
  const router = useRouter();
  return (
    <div
      style={{
        flex: 2,
        overflow: 'none',
        width: '100%',
        height: '100%',
        position: 'relative',
        border: 'none',
      }}
    >
      <ActiveChatTitle>
        {`Leasing Report ${demo ? '(demo)' : ''} - ${
          demo ? '1234 Marble St.' : leasingReport?.property.address.addr1
        }`}
      </ActiveChatTitle>
      {leasingReport && !demo && (
        <MessageList>
          {leasingReport.reports.map((report) => {
            let msg;
            if (report.startDate) {
              const now = new Date();
              const date = new Date(report.startDate);
              const week = date.toLocaleDateString();
              const endWeekDate = new Date(
                date.getFullYear(),
                date.getMonth(),
                date.getDate() + 7,
              );
              const endWeek =
                endWeekDate.getTime() < now.getTime() ?
                  endWeekDate.toLocaleDateString() :
                  now.toLocaleDateString();
              msg = `${
                date.getMonth() === now.getMonth() &&
                now.getDate() - date.getDate() < 7 ?
                  `Hi, here's your current progress report for this week ${week} ~ now:` :
                  `Hi, here's your weekly progress report for week ${week} - ${endWeek}:`
              }\nNew inquiries: ${report.numInquiries}\nNew scheduled tours: ${
                report.numScheduledTours
              }\nCompleted tours: ${
                report.numCompletedTours
              }\nNew Applications: ${report.numApplications}`;
            } else {
              msg = `This is your current status report for this property\nInquiries: ${report.numInquiries}\nScheduled tours: ${report.numScheduledTours}\nCompleted tours: ${report.numCompletedTours}\nApplications: ${report.numApplications}`;
            }
            return (
              <MessageListItemNew
                key={report.startDate ? report.startDate.toString() : 'total'}
                text={msg}
                recent={!report.startDate}
                sender={false}
                date={
                  report.startDate ? new Date(report.startDate) : new Date()
                }
              />
            );
          })}
        </MessageList>
      )}
      {demo && (
        <MessageList>
          {demoReports.map((report) => {
            let msg;
            if (report.startDate) {
              const now = new Date();
              const date = new Date(report.startDate);
              const week = date.toLocaleDateString();
              const endWeekDate = new Date(
                date.getFullYear(),
                date.getMonth(),
                date.getDate() + 7,
              );
              const endWeek =
                endWeekDate.getTime() < now.getTime() ?
                  endWeekDate.toLocaleDateString() :
                  now.toLocaleDateString();
              msg = `${
                date.getMonth() === now.getMonth() &&
                now.getDate() - date.getDate() < 7 ?
                  `Hi, here's your current progress report for this week ${week} ~ now:` :
                  `Hi, here's your weekly progress report for week ${week} - ${endWeek}:`
              }\nNew inquiries: ${report.numInquiries}\nNew scheduled tours: ${
                report.numScheduledTours
              }\nCompleted tours: ${
                report.numCompletedTours
              }\nNew Applications: ${report.numApplications}`;
            } else {
              msg = `This is your current status report for this property\nInquiries: ${report.numInquiries}\nScheduled tours: ${report.numScheduledTours}\nCompleted tours: ${report.numCompletedTours}\nApplications: ${report.numApplications}`;
            }
            return (
              <MessageListItemNew
                key={report.startDate ? report.startDate.toString() : 'total'}
                text={msg}
                recent={!report.startDate}
                sender={false}
                date={
                  report.startDate ? new Date(report.startDate) : new Date()
                }
              />
            );
          })}
        </MessageList>
      )}
      <div
        style={{
          bottom: 0,
          zIndex: 1,
          width: '100%',
          paddingTop: theme.spacing(2),
          paddingBottom: theme.spacing(2),
          paddingLeft: theme.spacing(4),
          paddingRight: theme.spacing(4),
        }}
      >
        <div style={{ width: '100%', display: 'flex' }}>
          <Grid container justifyContent="flex-end">
            <Grid item>
              <MarbleButton
                icon={<CaretRight />}
                variant="contained"
                onClick={() => {
                  if (supportChatId) {
                    router.push(`/owner/inbox/${supportChatId}`);
                  } else {
                    router.push('/owner/inbox');
                  }
                }}
                title="Go to Support Chat"
              />
            </Grid>
          </Grid>
        </div>
      </div>
    </div>
  );
};
